/**
 * A single reading of a monotonically increasing counter diagnostic, such as total fuel used or
 * total driving energy out.
 */
export interface CounterSample {
  dateTime: Date;
  value: number;
}

export interface CounterUsage {
  /**
   * What the counter says was used over the readings it answered with, or `null` when that cannot
   * be told. One reading gives a level, not a consumption, so two are the minimum.
   */
  used: number | null;
  /** How many usable readings came back. Zero means the vehicle reported nothing at all. */
  sampleCount: number;
  /** The window the readings actually span — not the window that was asked for. */
  measuredFrom: Date | null;
  measuredTo: Date | null;
  /**
   * How often a reading was lower than the one before it. A counter only ever counts up, so this
   * means it was reset, the telematics device was swapped, or a reading is corrupt. The usage is
   * then summed per run of rising readings, which is right for a reset and wrong for a corrupt
   * spike — either way the caller has to be told, hence this count.
   */
  resetCount: number;
}

const EMPTY: CounterUsage = {
  used: null,
  sampleCount: 0,
  measuredFrom: null,
  measuredTo: null,
  resetCount: 0,
};

/**
 * Works out how much a counter diagnostic counted up over the readings it returned.
 *
 * The readings do not have to be sorted or complete: Geotab answers with what the vehicle
 * happened to report, which for a counter diagnostic can be nothing at all, a single value, or a
 * stretch that covers only part of the period asked for. Every one of those cases has to stay
 * distinguishable from "used nothing", which is why this returns the measured window and the
 * reading count alongside the figure rather than just a number.
 *
 * @param samples - The readings, in any order.
 * @returns See {@link CounterUsage}.
 */
export function counterUsage(samples: CounterSample[]): CounterUsage {
  const usable = samples.filter(
    sample => Number.isFinite(sample.value) && Number.isFinite(sample.dateTime.getTime()),
  );
  if (usable.length === 0) return EMPTY;

  const sorted = [...usable].sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
  const measuredFrom = sorted[0].dateTime;
  const measuredTo = sorted[sorted.length - 1].dateTime;

  if (sorted.length === 1) {
    return { used: null, sampleCount: 1, measuredFrom, measuredTo, resetCount: 0 };
  }

  let used = 0;
  let resetCount = 0;
  let runStart = sorted[0].value;

  for (let index = 1; index < sorted.length; index++) {
    const previous = sorted[index - 1].value;
    const current = sorted[index].value;
    if (current < previous) {
      used += previous - runStart;
      runStart = current;
      resetCount++;
    }
  }
  used += sorted[sorted.length - 1].value - runStart;

  return { used, sampleCount: sorted.length, measuredFrom, measuredTo, resetCount };
}
